import React, { useEffect, useRef } from 'react';
import { sendLead, sendTrial } from './sendLead';
import { track } from './track';
import { getJourney } from './journey';

// Estrela do avatar da Digi
const Star = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M12 2l1.8 6.2L20 10l-6.2 1.8L12 18l-1.8-6.2L4 10l6.2-1.8L12 2z" fill="#fff" />
  </svg>
);

// Dika = IA de verdade (agente ElevenLabs, voz + texto) que qualifica o lead e
// registra empresa/cliente/ticket direto no Pipeline Comercial. A Digi oferece a
// Dika NA ABERTURA (antes do formulário) para o visitante seguir por um caminho
// só — o formulário da Digi também cria ticket, e os dois juntos duplicariam o lead.
const DIKA_AGENT_ID = process.env.REACT_APP_DIKA_AGENT_ID || 'agent_5001m3q3p073e6fs0sddaavedhyd';
const DIKA_WIDGET_SRC = 'https://unpkg.com/@elevenlabs/convai-widget-embed@0.18.3';

// Abre a Dika. Se o widget não carregar (CSP, bloqueador, rede), chama onFail
// para a Digi reabrir — o visitante nunca fica sem resposta.
function openDika(onFail) {
  document.body.classList.remove('digi-open');
  document.body.classList.add('dika-open');
  let failed = false;
  const fail = (reason) => {
    if (failed || customElements.get('elevenlabs-convai')) return;
    failed = true;
    document.querySelectorAll('script[data-dika], elevenlabs-convai').forEach((el) => el.remove());
    document.body.classList.remove('dika-open');
    if (onFail) onFail(reason);
  };
  if (!document.querySelector('script[data-dika]')) {
    const sc = document.createElement('script');
    sc.src = DIKA_WIDGET_SRC;
    sc.async = true;
    sc.type = 'text/javascript';
    sc.setAttribute('data-dika', '1');
    sc.onerror = () => fail('script_error');
    document.body.appendChild(sc);
  }
  if (!document.querySelector('elevenlabs-convai')) {
    const w = document.createElement('elevenlabs-convai');
    w.setAttribute('agent-id', DIKA_AGENT_ID);
    // Jornada do visitante: vai para a Dika como dynamic variable e chega no
    // post-call do ms-landingpage (grava em jornada_no_site). Só é enviada se o
    // visitante aceitar o termo do widget e iniciar a conversa.
    // Vai só o resumo (sem jornada_completa) e sempre com a variável presente
    // ('' = sem jornada), para a conversa nunca falhar por variável ausente.
    const journey = getJourney({ canal: 'dika', maxFullBytes: 0 });
    w.setAttribute('dynamic-variables', JSON.stringify({ site_journey: journey ? JSON.stringify(journey) : '' }));
    document.body.appendChild(w);
  }
  setTimeout(() => fail('timeout'), 8000);
}

/**
 * Chatbot "Digi" — conversa guiada 100% por botões (sem texto livre).
 *
 * É um funil de demonstração: começa pedindo o contato (introForm), captura o
 * lead e segue por atalhos (`nodes`) até um nó terminal (`end: true`) que
 * encerra com a mensagem de fechamento. Sem input de texto (o bot é scriptado,
 * não há IA real aqui), sem loops, sem mensagens automáticas.
 *
 * Lead: no submit do introForm o `lead` e enviado ao ms-landingpage (ver
 * `sendLead.js`). TODO: IA real — trocar `nodes`/`go` pelo bot da Digitalk.
 */
export default function Digi() {
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return undefined;
    ran.current = true;

    const body = document.getElementById('digiBody');
    if (!body) return undefined;

    let started = false;
    let lead = {};
    let trial = {};
    const firstName = () => (lead.name || '').trim().split(/\s+/)[0] || 'você';

    // Funil finito. Nós com `end:true` encerram a conversa (nenhum aponta de
    // volta pra um nó anterior -> nunca cicla).
    const nodes = {
      menu: {
        msg: 'Prazer, {n}! 🙌 Como posso te ajudar?',
        chips: [['🚀 Quero iniciar um teste', 'trial'], ['Ver a plataforma', 'plataforma'], ['Sou parceiro / consultoria', 'parceiro'], ['Só explorando', 'explorando']],
      },
      plataforma: {
        msg: 'A Digitalk é a plataforma que *opera* as suas jornadas: você modela os dados, desenha o fluxo em Kanban, atende em todos os canais e deixa a IA triar, monitorar e pontuar cada cliente.\n\nO que quer entender melhor?',
        chips: [['Como a IA funciona', 'ia'], ['Quanto custa', 'custo'], ['Quero uma demonstração', 'demo']],
      },
      ia: {
        msg: 'A IA aqui não é um chatbot no canto 😉 Ela tria o ticket e escolhe o destino, monitora a qualidade, lê CSAT/NPS e dá um Score de risco por cliente — tudo dentro da operação.',
        chips: [['Quanto custa', 'custo'], ['Quero uma demonstração', 'demo']],
      },
      custo: {
        msg: 'A Digitalk entra no lugar de 6 a 8 ferramentas (CRM, helpdesk, telefonia, automação, BI…). O custo total costuma ser uma fração do que você paga hoje somado.',
        chips: [['🚀 Iniciar um teste grátis', 'trial'], ['Quero uma demonstração', 'demo']],
      },
      parceiro: {
        msg: 'Adoro 🤝 A Digitalk é grande demais pra vender num checkout — ela pede diagnóstico e implantação. Pra consultoria certa, isso é margem, recorrência e cliente fiel.\n\nComo prefere seguir?',
        chips: [['Quero revender / implantar', 'lead_parceiro'], ['Receber a apresentação', 'materiais']],
      },
      explorando: {
        msg: 'Fica à vontade 🙂 O que mais impressiona é a automação visual, a IA de triagem e a telefonia com discador nativo. Por onde começamos?',
        chips: [['Ver a plataforma', 'plataforma'], ['Sou parceiro', 'parceiro']],
      },
      // ---- nós terminais (encerram a conversa) ----
      demo: {
        msg: 'Perfeito, {n}! 🚀 Já tenho seu contato — um especialista te chama ainda hoje pra montar a demonstração na sua operação. Até já! 👋',
        end: true,
      },
      lead_parceiro: {
        msg: 'Show, {n}! Já anotei seu contato — o time de canais te chama pra desenhar a parceria. 🤝',
        end: true,
      },
      materiais: {
        msg: 'Feito, {n}! Vou te mandar a apresentação do programa de parceiros no seu e-mail. 📩',
        end: true,
      },
      trial_done: {
        msg: '🎉 Ambiente solicitado, {n}! Coloquei a *{c}* na nossa fila. Em até *48 horas* nosso time entra em contato com as credenciais do seu ambiente *{s}.digitalk.com.br*.\n\nFica de olho no WhatsApp e no e-mail. Até já! 👋',
        end: true,
      },
    };

    const scrollDown = () => { body.scrollTop = body.scrollHeight; };
    const pv = (t) => (t || '')
      .replace(/\{n\}/g, firstName())
      .replace(/\{c\}/g, trial.company || 'sua empresa')
      .replace(/\{s\}/g, trial.subdomain || 'seu-ambiente');
    const addMsg = (text, who) => {
      const d = document.createElement('div');
      d.className = 'msg ' + who;
      d.innerHTML = pv(text).replace(/\n/g, '<br>').replace(/\*(.+?)\*/g, '<b>$1</b>');
      body.appendChild(d);
      scrollDown();
    };
    const removeChips = () => { body.querySelectorAll('.chips').forEach((e) => e.remove()); };
    const addChips = (chips) => {
      const wrap = document.createElement('div');
      wrap.className = 'chips';
      chips.forEach((c) => {
        const b = document.createElement('button');
        b.className = 'chip';
        b.textContent = c[0];
        b.onclick = () => { track('digi_choice', { choice: c[1], label: c[0] }); removeChips(); addMsg(c[0], 'me'); if (c[1] === 'trial') trialForm(); else if (c[1] === 'dika') { track('dika_open', { from: 'digi' }); openDika(dikaFail); } else if (c[1] === 'form') introForm(); else go(c[1]); };
        wrap.appendChild(b);
      });
      body.appendChild(wrap);
      scrollDown();
    };
    const typing = (cb) => {
      const t = document.createElement('div');
      t.className = 'typing';
      t.innerHTML = '<i></i><i></i><i></i>';
      body.appendChild(t);
      scrollDown();
      setTimeout(() => { t.remove(); cb(); }, 700);
    };
    function go(id) {
      const n = nodes[id];
      if (!n) return;
      if (n.end) track('digi_outcome', { outcome: id });
      typing(() => {
        addMsg(n.msg, 'bot');
        if (n.chips) addChips(n.chips);
      });
    }

    const dikaFail = (reason) => {
      track('dika_error', { reason });
      document.body.classList.add('digi-open');
      addMsg('Ops, não consegui abrir a Dika agora 😕 Sem problema: me deixa seu contato que um especialista te chama.', 'bot');
      introForm();
    };

    const introChoice = () => {
      typing(() => {
        addMsg('Oi! Eu sou a Digi 👋 Quer conversar agora com a *Dika*, a inteligência artificial da Digitalk? Ela tira suas dúvidas por *voz ou texto* e já agenda uma apresentação. Se preferir, eu anoto seu contato e um especialista te chama.', 'bot');
        addChips([['🎙️ Conversar com a Dika (IA)', 'dika'], ['📝 Prefiro deixar meu contato', 'form']]);
      });
    };

    const introForm = () => {
      typing(() => {
        addMsg('Combinado! Como te encontro depois? Assim um especialista te acompanha no canal que preferir — WhatsApp, e-mail ou ligação.', 'bot');
        const f = document.createElement('form');
        f.className = 'digi-form';
        f.setAttribute('novalidate', '');
        f.innerHTML =
          HP +
          '<label>Seu nome</label><input name="nome" placeholder="Ex.: Rafael Bianchi" autocomplete="name">' +
          '<label>WhatsApp <small style="color:#9b95b3;font-weight:400">· fora do BR: use +</small></label><input name="fone" placeholder="(11) 90000-0000" inputmode="tel" autocomplete="tel">' +
          '<label>E-mail</label><input name="mail" placeholder="voce@empresa.com" inputmode="email" autocomplete="email">' +
          '<p class="digi-consent">Ao enviar, você concorda que a Digitalk use seus dados de contato e as páginas que você visitou neste site para entender seu interesse e falar com você. <a href="/politicas-de-privacidade#site" target="_blank" rel="noopener">Política de Privacidade</a></p>' +
          '<div class="err"></div><button type="submit">Bora conversar 🚀</button>';
        attachMask(f.fone, maskPhone);
        const shownAt = Date.now();
        f.onsubmit = (e) => {
          e.preventDefault();
          const nome = f.nome.value.trim();
          const fone = f.fone.value.trim();
          const mail = f.mail.value.trim();
          const okMail = /[^\s@]+@[^\s@]+\.[^\s@]+/.test(mail);
          const err = f.querySelector('.err');
          if (nome.length < 2) { err.textContent = 'Me diz seu nome 🙂'; return; }
          if (!phoneOk(fone)) { err.textContent = fone.charAt(0) === '+' ? 'Confere o telefone com o código do país 🌎' : 'Confere o WhatsApp com DDD 📱'; return; }
          if (!okMail) { err.textContent = 'Confere o e-mail ✉️'; return; }
          lead = { name: nome, phone: fone, email: mail };
          // Conversão principal (sem PII no dataLayer — só flags). Não envia se parecer bot.
          if (!isBot(f, shownAt)) {
            track('lead_captured', { method: 'digi_form', has_phone: phoneOk(fone), has_email: okMail });
            sendLead(lead).catch(() => {});
          }
          f.remove();
          addMsg(nome, 'me');
          go('menu');
        };
        body.appendChild(f);
        scrollDown();
        setTimeout(() => { f.nome.focus(); }, 100);
      });
    };

    const slug = (v) => (v || '').toLowerCase().normalize('NFD')
      .replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-');
    // Máscaras (limitam e formatam enquanto digita).
    const maskPhone = (v) => {
      const raw = v || '';
      // Internacional: começou com "+" -> mantém + e dígitos (sem máscara BR).
      if (raw.trim().charAt(0) === '+') return '+' + raw.replace(/[^\d]/g, '').slice(0, 15);
      const d = raw.replace(/\D/g, '').slice(0, 11);
      if (!d) return '';
      if (d.length <= 2) return '(' + d;
      if (d.length <= 6) return '(' + d.slice(0, 2) + ') ' + d.slice(2);
      if (d.length <= 10) return '(' + d.slice(0, 2) + ') ' + d.slice(2, 6) + '-' + d.slice(6);
      return '(' + d.slice(0, 2) + ') ' + d.slice(2, 7) + '-' + d.slice(7);
    };
    // Anti-spam (front): honeypot preenchido OU envio rápido demais = provável bot.
    const HP = '<input name="hp" tabindex="-1" autocomplete="off" aria-hidden="true" style="position:absolute;left:-9999px;width:1px;height:1px;opacity:0">';
    // Anti-spam: só o time-trap. O honeypot foi desativado porque o autofill/
    // gerenciador de senhas do navegador preenchia o campo oculto e derrubava
    // leads reais (falso-positivo). Proteção mais forte = Turnstile (a plugar).
    const isBot = (f, shownAt) => (Date.now() - shownAt < 2500);
    // Valida telefone: BR (>=10 díg.) ou internacional (começa com +, >=8 díg.).
    const phoneOk = (v) => { const t = (v || '').trim(); const n = (t.match(/\d/g) || []).length; return t.charAt(0) === '+' ? n >= 8 : n >= 10; };
    // CNPJ alfanumérico (padrão 2026): 12 primeiras posições A-Z/0-9, 2 verificadores numéricos.
    const maskCNPJ = (v) => {
      const c = (v || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 14);
      let r = c;
      if (c.length > 2) r = c.slice(0, 2) + '.' + c.slice(2);
      if (c.length > 5) r = c.slice(0, 2) + '.' + c.slice(2, 5) + '.' + c.slice(5);
      if (c.length > 8) r = c.slice(0, 2) + '.' + c.slice(2, 5) + '.' + c.slice(5, 8) + '/' + c.slice(8);
      if (c.length > 12) r = c.slice(0, 2) + '.' + c.slice(2, 5) + '.' + c.slice(5, 8) + '/' + c.slice(8, 12) + '-' + c.slice(12);
      return r;
    };
    const attachMask = (el, fn) => { if (el) el.addEventListener('input', () => { el.value = fn(el.value); }); };

    // Fluxo de solicitacao de ambiente (teste): cadastro completo empresa+pessoa+
    // subdominio -> cria o lead na fase Inbound/"Solicitou ambiente".
    const trialForm = () => {
      typing(() => {
        addMsg('Boa! Vamos preparar o seu *ambiente de teste* 🚀 Me passa os dados da empresa e de quem vai usar — em até 48h enviamos as credenciais.', 'bot');
        const f = document.createElement('form');
        f.className = 'digi-form';
        f.setAttribute('novalidate', '');
        f.innerHTML =
          HP +
          '<label>Nome da empresa</label><input name="empresa" placeholder="Ex.: Acme Ltda" autocomplete="organization">' +
          '<label>CNPJ</label><input name="cnpj" placeholder="00.000.000/0000-00" autocapitalize="characters">' +
          '<label>Seu nome</label><input name="nome" placeholder="Ex.: Rafael Bianchi" autocomplete="name">' +
          '<label>Cargo (opcional)</label><input name="cargo" placeholder="Ex.: Head de Atendimento">' +
          '<label>E-mail corporativo</label><input name="mail" placeholder="voce@empresa.com" inputmode="email" autocomplete="email">' +
          '<label>WhatsApp <small style="color:#9b95b3;font-weight:400">· fora do BR: use +</small></label><input name="fone" placeholder="(11) 90000-0000" inputmode="tel" autocomplete="tel">' +
          '<label>Nome do ambiente</label><div class="digi-sub"><input name="sub" placeholder="suaempresa" autocapitalize="none" autocorrect="off" spellcheck="false"><span>.digitalk.com.br</span></div>' +
          '<p class="digi-consent">Ao enviar, você concorda que a Digitalk use seus dados de contato e as páginas que você visitou neste site para entender seu interesse e falar com você. <a href="/politicas-de-privacidade#site" target="_blank" rel="noopener">Política de Privacidade</a></p>' +
          '<div class="err"></div><button type="submit">Solicitar meu ambiente 🚀</button>';
        const shownAt = Date.now();
        if (lead.name) f.nome.value = lead.name;
        if (lead.phone) f.fone.value = lead.phone;
        if (lead.email) f.mail.value = lead.email;
        f.sub.addEventListener('input', () => { f.sub.value = slug(f.sub.value); });
        attachMask(f.cnpj, maskCNPJ);
        attachMask(f.fone, maskPhone);
        f.onsubmit = (e) => {
          e.preventDefault();
          const empresa = f.empresa.value.trim();
          const nome = f.nome.value.trim();
          const mail = f.mail.value.trim();
          const fone = f.fone.value.trim();
          const sub = slug(f.sub.value.trim());
          const okMail = /[^\s@]+@[^\s@]+\.[^\s@]+/.test(mail);
          const err = f.querySelector('.err');
          if (empresa.length < 2) { err.textContent = 'Qual o nome da empresa? 🏢'; return; }
          if (f.cnpj.value.replace(/[^A-Za-z0-9]/g, '').length !== 14) { err.textContent = 'Confere o CNPJ (14 caracteres) 🧾'; return; }
          if (nome.length < 2) { err.textContent = 'Me diz seu nome 🙂'; return; }
          if (!okMail) { err.textContent = 'Confere o e-mail corporativo ✉️'; return; }
          if (!phoneOk(fone)) { err.textContent = fone.charAt(0) === '+' ? 'Confere o telefone com o código do país 🌎' : 'Confere o WhatsApp com DDD 📱'; return; }
          if (sub.length < 2) { err.textContent = 'Escolhe o nome do ambiente (ex.: suaempresa) 🌐'; return; }
          trial = { company: empresa, cnpj: f.cnpj.value.trim(), name: nome, role: f.cargo.value.trim(), email: mail, phone: fone, subdomain: sub, product: 'Digitalk Plataforma' };
          lead = { name: nome, phone: fone, email: mail };
          if (!isBot(f, shownAt)) {
            track('trial_submitted', { method: 'digi_form', has_cnpj: !!trial.cnpj, has_role: !!trial.role });
            sendTrial(trial).catch(() => {});
          }
          f.remove();
          addMsg('Solicitação enviada ✅', 'me');
          go('trial_done');
        };
        body.appendChild(f);
        scrollDown();
        setTimeout(() => { f.empresa.focus(); }, 100);
      });
    };

    const openPanel = () => {
      document.body.classList.add('digi-open');
      track('digi_open');
      if (!started) { started = true; introChoice(); }
    };
    const openTrial = () => {
      document.body.classList.add('digi-open');
      track('digi_open', { intent: 'trial' });
      started = true;
      trialForm();
    };
    const closePanel = () => { document.body.classList.remove('digi-open'); };

    window.openDigi = openPanel;
    window.openDigiTrial = openTrial;
    window.openDika = () => { track('dika_open', { from: 'cta' }); openDika(dikaFail); };
    document.getElementById('digiLaunch').onclick = openPanel;
    document.getElementById('digiClose').onclick = closePanel;

    return () => { delete window.openDigi; delete window.openDigiTrial; delete window.openDika; };
  }, []);

  return (
    <>
      <button className="digi-launch" id="digiLaunch" aria-label="Falar com a Digi">
        <span className="pls" />
        <span className="av"><Star size={18} /></span>
        Falar com a Digi
      </button>

      <section className="digi-panel" id="digiPanel" aria-label="Chat com a Digi" role="dialog">
        <div className="digi-head">
          <div className="av">
            <Star size={22} />
            <span className="on" />
          </div>
          <div>
            <h4>Digi</h4>
            <p>Assistente da Digitalk · online</p>
          </div>
          <button className="digi-x" id="digiClose" aria-label="Fechar">✕</button>
        </div>
        <div className="digi-body" id="digiBody" />
        <div className="digi-note">Experiência de demonstração · a Digi de produção conecta na IA da Digitalk</div>
      </section>
    </>
  );
}
